import { PERMISSIONS } from '../../../constants/permissions';
import { mntLabel } from '../../../interfaces/maintenance';
import { fmtDateTime, fmtMinutes, money } from '../woCommon';
import { groupLabel } from '../useEquipmentGroups';

/** Filter controls a report can show. The viewer renders each one as a flex cell. */
export type FilterKey =
    | 'from' | 'to' | 'bucket' | 'groupBy' | 'equipmentId' | 'departmentId' | 'status' | 'typeId' | 'category'
    | 'ownership' | 'idleOnly' | 'days' | 'limit' | 'meterId';

export interface ReportColumn {
    header: string;
    /** Plain text so the table, tests and CSV-style reading agree. */
    value: (row: any) => string; // eslint-disable-line @typescript-eslint/no-explicit-any
    numeric?: boolean;
}

export interface ReportDef {
    slug: string;
    label: string;
    blurb: string;
    filters: FilterKey[];
    paged: boolean;
    /** Utility reports are also open to a meter reader (READING_RECORD); everything else needs REPORT_VIEW. */
    utility?: boolean;
    groupOptions?: string[];
    bucketOptions?: string[];
    columns: ReportColumn[];
    /** Applied defaults that are not dates. */
    defaults?: Record<string, string>;
}

const n = (v?: number | null, digits = 0): string =>
    v === undefined || v === null ? '-' : Number(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
const t = (v?: string | null): string => (v === undefined || v === null || v === '' ? '-' : v);
const pct = (v?: number | null): string => (v === undefined || v === null ? '-' : `${v.toFixed(1)}%`);

export const REPORT_DEFS: ReportDef[] = [
    {
        slug: 'breakdown-log', label: 'Breakdown log', paged: true, filters: ['from', 'to', 'equipmentId', 'departmentId', 'status'],
        blurb: 'Every breakdown work order in the period: when it stopped, who fixed it, parts used and cost.',
        columns: [
            { header: 'WO', value: r => t(r.woNo) },
            { header: 'Status', value: r => mntLabel(r.status) },
            { header: 'Machine', value: r => `${t(r.equipmentCode)} ${r.equipmentName ?? ''}`.trim() },
            { header: 'Line', value: r => t(r.line) },
            { header: 'Reported', value: r => fmtDateTime(r.reportedAt) },
            { header: 'Stopped', value: r => fmtDateTime(r.stoppedAt) },
            { header: 'Back in service', value: r => fmtDateTime(r.backInServiceAt) },
            { header: 'Downtime', value: r => fmtMinutes(r.downtimeMinutes), numeric: true },
            { header: 'Symptom', value: r => t(r.symptom) },
            { header: 'Cause', value: r => t(r.cause) },
            { header: 'Action', value: r => t(r.action) },
            { header: 'Technician', value: r => t(r.technician) },
            { header: 'Parts', value: r => t(r.partsSummary) },
            { header: 'Parts value', value: r => money(r.partsValue), numeric: true },
            { header: 'External cost', value: r => money(r.externalCostMemo), numeric: true },
        ],
    },
    {
        slug: 'downtime', label: 'Downtime', paged: false, filters: ['from', 'to', 'bucket', 'groupBy', 'equipmentId'],
        blurb: 'Stopped minutes and stoppages by day, week or month, grouped by line, machine type or machine.',
        groupOptions: ['LOCATION', 'TYPE', 'MACHINE'], bucketOptions: ['DAY', 'WEEK', 'MONTH'],
        columns: [
            { header: 'Group', value: r => t(r.group) },
            { header: 'Period', value: r => t(r.period) },
            { header: 'Stoppages', value: r => n(r.stoppages), numeric: true },
            { header: 'Downtime', value: r => fmtMinutes(r.minutes), numeric: true },
        ],
    },
    {
        slug: 'mttr-mtbf', label: 'MTTR and MTBF', paged: false, filters: ['from', 'to', 'groupBy', 'typeId'],
        blurb: 'Mean time to repair (minutes) and between failures (hours). MTBF needs at least 2 breakdowns.',
        groupOptions: ['MACHINE', 'TYPE'],
        columns: [
            { header: 'Code', value: r => t(r.code) },
            { header: 'Name', value: r => t(r.name) },
            { header: 'Type', value: r => t(r.type) },
            { header: 'Breakdowns', value: r => n(r.breakdowns), numeric: true },
            { header: 'Downtime', value: r => fmtMinutes(r.downtimeMinutes), numeric: true },
            { header: 'MTTR (min)', value: r => n(r.mttrMinutes, 1), numeric: true },
            { header: 'MTBF (h)', value: r => (r.insufficientData ? 'Not enough data' : n(r.mtbfHours, 1)), numeric: true },
        ],
    },
    {
        slug: 'top-problems', label: 'Top problem machines', paged: false, filters: ['days', 'limit'],
        blurb: 'The machines that broke down most in the last N days.',
        defaults: { days: '90', limit: '10' },
        columns: [
            { header: '#', value: r => n(r.rank), numeric: true },
            { header: 'Code', value: r => t(r.code) },
            { header: 'Name', value: r => t(r.name) },
            { header: 'Type', value: r => t(r.type) },
            { header: 'Line', value: r => t(r.line) },
            { header: 'Breakdowns', value: r => n(r.breakdowns), numeric: true },
            { header: 'Downtime', value: r => fmtMinutes(r.downtimeMinutes), numeric: true },
        ],
    },
    {
        slug: 'pm-compliance', label: 'PM compliance', paged: false, filters: ['from', 'to', 'groupBy'],
        blurb: 'Preventive work done on time against due. Not-yet-due work is left out of the percentage.',
        groupOptions: ['MONTH', 'PLAN'],
        columns: [
            { header: 'Group', value: r => t(r.label) },
            { header: 'Due', value: r => n(r.due), numeric: true },
            { header: 'On time', value: r => n(r.onTime), numeric: true },
            { header: 'Late', value: r => n(r.late), numeric: true },
            { header: 'Open overdue', value: r => n(r.openOverdue), numeric: true },
            { header: 'Upcoming', value: r => n(r.upcoming), numeric: true },
            { header: 'On time %', value: r => pct(r.onTimePercent), numeric: true },
        ],
    },
    {
        slug: 'backlog', label: 'Open work backlog', paged: false, filters: [],
        blurb: 'Open work orders by status and age, as of today.',
        columns: [
            { header: 'Status', value: r => mntLabel(r.status) },
            { header: 'Up to 2 days', value: r => n(r.upTo2Days), numeric: true },
            { header: '3-7 days', value: r => n(r.days3To7), numeric: true },
            { header: '8-14 days', value: r => n(r.days8To14), numeric: true },
            { header: '15-30 days', value: r => n(r.days15To30), numeric: true },
            { header: 'Over 30 days', value: r => n(r.over30Days), numeric: true },
            { header: 'Total', value: r => n(r.total), numeric: true },
        ],
    },
    {
        slug: 'parts-used', label: 'Parts used', paged: false, filters: ['from', 'to', 'groupBy'],
        blurb: 'Spare parts issued to maintenance by month, with the posted stock value.',
        groupOptions: ['MACHINE', 'TYPE'],
        columns: [
            { header: 'Month', value: r => t(r.month) },
            { header: 'Code', value: r => t(r.code) },
            { header: 'Name', value: r => t(r.name) },
            { header: 'Quantity', value: r => n(r.qty), numeric: true },
            { header: 'Value', value: r => money(r.value), numeric: true },
        ],
    },
    {
        slug: 'unlinked-issues', label: 'Unlinked store issues', paged: true, filters: ['from', 'to'],
        blurb: 'Issues from the Maintenance store that no work order claims. Chase these.',
        columns: [
            { header: 'Requisition', value: r => t(r.refNo) },
            { header: 'Remarks', value: r => t(r.remarks) },
            { header: 'Last issued', value: r => fmtDateTime(r.lastIssuedAt) },
            { header: 'Quantity', value: r => n(r.qty), numeric: true },
            { header: 'Requisition value (all lines)', value: r => money(r.requisitionValue), numeric: true },
        ],
    },
    {
        slug: 'utility-consumption', label: 'Utility consumption', paged: true, utility: true,
        filters: ['from', 'to', 'bucket', 'equipmentId', 'meterId'], bucketOptions: ['DAY', 'MONTH', 'TOTAL'],
        blurb: 'Electricity, gas, water and similar meters: totals for counters, min, average and max for gauges.',
        columns: [
            { header: 'Machine', value: r => `${t(r.equipmentCode)} ${r.equipmentName ?? ''}`.trim() },
            { header: 'Meter', value: r => t(r.meterName) },
            { header: 'Kind', value: r => mntLabel(r.kind) },
            { header: 'Period', value: r => t(r.period) },
            { header: 'Total', value: r => n(r.total, 2), numeric: true },
            { header: 'Min', value: r => n(r.min, 2), numeric: true },
            { header: 'Average', value: r => n(r.avg, 2), numeric: true },
            { header: 'Max', value: r => n(r.max, 2), numeric: true },
            { header: 'Unit', value: r => t(r.unit) },
            { header: 'Readings', value: r => n(r.readings), numeric: true },
        ],
    },
    {
        slug: 'utility-out-of-range', label: 'Out-of-range readings', paged: true, utility: true, filters: ['from', 'to'],
        blurb: 'Readings outside the allowed range set on the meter.',
        columns: [
            { header: 'Date', value: r => t(r.date) },
            { header: 'Machine', value: r => `${t(r.equipmentCode)} ${r.equipmentName ?? ''}`.trim() },
            { header: 'Meter', value: r => t(r.meterName) },
            { header: 'Value', value: r => n(r.value, 2), numeric: true },
            { header: 'Min', value: r => n(r.minValue, 2), numeric: true },
            { header: 'Max', value: r => n(r.maxValue, 2), numeric: true },
            { header: 'Unit', value: r => t(r.unit) },
            { header: 'Note', value: r => t(r.note) },
        ],
    },
    {
        slug: 'generators', label: 'Generators', paged: false, filters: ['from', 'to'],
        blurb: 'Run hours, energy generated and fuel used per generator.',
        columns: [
            { header: 'Code', value: r => t(r.code) },
            { header: 'Name', value: r => t(r.name) },
            { header: 'Run hours', value: r => n(r.runHours, 1), numeric: true },
            { header: 'kWh', value: r => n(r.kwh, 1), numeric: true },
            { header: 'Fuel meter', value: r => t(r.fuelMeter) },
            { header: 'Fuel used', value: r => n(r.fuelUsed, 1), numeric: true },
            { header: 'Fuel / kWh', value: r => n(r.fuelPerKwh, 3), numeric: true },
            { header: 'kWh / run hour', value: r => n(r.kwhPerRunHour, 2), numeric: true },
        ],
    },
    {
        slug: 'equipment-register', label: 'Equipment register', paged: true,
        filters: ['typeId', 'category', 'status', 'ownership', 'idleOnly'],
        blurb: 'The machine list with ownership, rental end, warranty and idle flag.',
        columns: [
            { header: 'Code', value: r => t(r.code) },
            { header: 'Name', value: r => t(r.name) },
            { header: 'Type', value: r => t(r.type) },
            { header: 'Group', value: r => groupLabel(r.category) },
            { header: 'Status', value: r => mntLabel(r.status) },
            { header: 'Criticality', value: r => t(r.criticality) },
            { header: 'Ownership', value: r => mntLabel(r.ownership) },
            { header: 'Owner / lessor', value: r => t(r.ownerParty) },
            { header: 'Rental until', value: r => t(r.rentalUntil) },
            { header: 'Location', value: r => t(r.location) },
            { header: 'Fixed asset', value: r => t(r.fixedAsset) },
            { header: 'Warranty until', value: r => t(r.warrantyUntil) },
            { header: 'Idle', value: r => (r.idle ? 'Yes' : 'No') },
        ],
    },
];

/** Keys that open a report: REPORT_VIEW for all of them, READING_RECORD too for the utility ones. */
export const reportKeysFor = (def: ReportDef): string[] =>
    def.utility
        ? [PERMISSIONS.MAINTENANCE_REPORT_VIEW, PERMISSIONS.MAINTENANCE_READING_RECORD]
        : [PERMISSIONS.MAINTENANCE_REPORT_VIEW];
